import { useSessionContext } from "@/hooks/useSessionContext";
import { useSyncExternalStore } from "react";

// ─── Active-role override (localStorage) ─────────────────────────────────
// Users with multiple assigned roles can pick which one is "active" for the
// UI via the role switcher in the avatar dropdown. The choice persists in
// localStorage so refresh keeps it; signing out clears it.
const ACTIVE_ROLE_KEY = "grxbooks:active_role";

function getStoredActiveRole(): string | null {
  try { return localStorage.getItem(ACTIVE_ROLE_KEY); } catch { return null; }
}

function subscribeStorage(cb: () => void) {
  const handler = (e: StorageEvent) => { if (e.key === ACTIVE_ROLE_KEY) cb(); };
  window.addEventListener("storage", handler);
  window.addEventListener(ACTIVE_ROLE_KEY, cb as EventListener);
  return () => {
    window.removeEventListener("storage", handler);
    window.removeEventListener(ACTIVE_ROLE_KEY, cb as EventListener);
  };
}

export function useActiveRoleOverride(): string | null {
  return useSyncExternalStore(subscribeStorage, getStoredActiveRole, () => null);
}

export function setActiveRoleOverride(role: string | null) {
  try {
    if (role) localStorage.setItem(ACTIVE_ROLE_KEY, role);
    else localStorage.removeItem(ACTIVE_ROLE_KEY);
    // Same-tab listeners — the native "storage" event only fires for other tabs.
    window.dispatchEvent(new Event(ACTIVE_ROLE_KEY));
  } catch {
    /* localStorage unavailable — overrides become a no-op for this session */
  }
}

/**
 * All role hooks read from the session-context bootstrap (single round
 * trip on login, cached for entire session). No additional network calls.
 *
 * NOTE: roles in session-context are already org-scoped server-side.
 *
 * SUPER ADMIN BYPASS: Super admins ALWAYS pass every role gate. They may not
 * have any org-scoped user_roles entry yet still must retain full access.
 */

function useOrgRoles() {
  const { data, isLoading } = useSessionContext();
  return {
    orgRoles: data?.roles ?? [],
    orgId: data?.organizationId ?? null,
    isSuperAdmin: !!data?.isSuperAdmin,
    isLoading,
  };
}

export function useIsAdminOrHR() {
  const { orgRoles, orgId, isSuperAdmin, isLoading } = useOrgRoles();
  return {
    data: isSuperAdmin
      ? true
      : orgId
        ? orgRoles.some((r) => r === "admin" || r === "hr")
        : false,
    isLoading,
  };
}

export function useIsFinance() {
  const { orgRoles, orgId, isSuperAdmin, isLoading } = useOrgRoles();
  return {
    data: isSuperAdmin
      ? true
      : orgId
        ? orgRoles.some((r) => r === "admin" || r === "finance")
        : false,
    isLoading,
  };
}

export function useIsManager() {
  const { orgRoles, orgId, isSuperAdmin, isLoading } = useOrgRoles();
  return {
    data: isSuperAdmin ? true : orgId ? orgRoles.includes("manager") : false,
    isLoading,
  };
}

// Sorted list of roles actually assigned to the user. Used by the role
// switcher to know what choices to surface. Empty unless the user has org
// context. Super admins implicitly get "admin" added so the switcher can
// still demote them.
export function useAssignedRoles() {
  const { orgRoles, orgId, isSuperAdmin, isLoading } = useOrgRoles();
  const priority = ["admin", "hr", "finance", "payroll", "manager", "employee"];
  const set = new Set<string>(orgRoles);
  if (isSuperAdmin) set.add("admin");
  const roles = priority.filter((r) => set.has(r));
  return { data: orgId || isSuperAdmin ? roles : [], isLoading };
}

export function useCurrentRole() {
  const { orgRoles, orgId, isSuperAdmin, isLoading } = useOrgRoles();
  const override = useActiveRoleOverride();

  let role: string | null = null;
  if (isSuperAdmin) {
    // Super admins are treated as admin everywhere for role-gated UI.
    role = "admin";
  } else if (orgId) {
    if (orgRoles.includes("admin")) role = "admin";
    else if (orgRoles.includes("hr")) role = "hr";
    else if (orgRoles.includes("finance")) role = "finance";
    else if (orgRoles.includes("payroll")) role = "payroll";
    else if (orgRoles.includes("manager")) role = "manager";
    else role = "employee";
  }
  // Apply the localStorage override only if the user is actually assigned
  // that role — prevents privilege escalation by editing localStorage.
  if (override && (isSuperAdmin || orgRoles.includes(override))) {
    role = override;
  }
  return { data: role, isLoading };
}
